import { getToken, clearSession } from "./auth";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

type FetchOpts = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  json?: unknown;
  body?: BodyInit;
  headers?: Record<string, string>;
  auth?: boolean; // default true
};

async function request<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts.body !== undefined) {
    body = opts.body;
  }
  if (opts.auth !== false) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(path, { method: opts.method ?? "GET", headers, body });
  if (res.status === 401 && opts.auth !== false) {
    clearSession();
    // Soft redirect: drop to login. SPA will pick up on next render via ProtectedRoute.
    if (location.pathname !== "/login") {
      location.replace("/login");
    }
  }
  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  if (!res.ok) throw new ApiError(res.status, parsed);
  return parsed as T;
}

export const api = {
  get:    <T>(path: string)            => request<T>(path),
  post:   <T>(path: string, json: unknown) => request<T>(path, { method: "POST", json }),
  postForm: <T>(path: string, fd: FormData) => request<T>(path, { method: "POST", body: fd }),
  put:    <T>(path: string, json: unknown) => request<T>(path, { method: "PUT", json }),
  patch:  <T>(path: string, json: unknown) => request<T>(path, { method: "PATCH", json }),
  delete: <T>(path: string)            => request<T>(path, { method: "DELETE" }),
  // 移除/换图时安全删上传文件：后端全库复查，仍被已保存记录引用则保留，不误删。失败静默（留待孤立清理兜底）。
  dropFile: (url: string | null | undefined): Promise<unknown> => {
    if (!url) return Promise.resolve();
    return request("/api/admin/uploads/delete", { method: "POST", json: { url } }).catch(() => {});
  },
  upload: <T>(path: string, file: File, prefix: string) => {
    const fd = new FormData();
    fd.append("file", file);
    const url = `${path}?prefix=${encodeURIComponent(prefix)}`;
    return request<T>(url, { method: "POST", body: fd });
  },
};
